/*
  Gaia Iluka — Creator Circle: overlay application submission
  File: assets/gi-cc-overlay.js

  NEW (visual-first pass, 3 Sep 2026). Progressive enhancement only. The
  form inside sections/gi-cc-overlay.liquid is Shopify's own contact form
  ({% form 'contact' %}) with a real action and method, the hidden
  contact[Enquiry] value and a required email. If this script never runs,
  the form still submits natively and the section's Liquid-rendered
  .cc-form-success / .cc-form-error blocks render on reload. This script
  never touches the form's fields, action, method, required attribute or
  contact[Enquiry] value.

  HOTFIX (Oct 2026): CAPTCHA-AWARE SUBMISSION.

  Root cause. The store's contact forms are protected by Shopify's
  storefront hCaptcha (cdn.shopify.com/shopifycloud/storefront-forms-
  hcaptcha). Every POST to /contact must carry a valid h-captcha-response;
  without one Shopify answers 400 "Missing CAPTCHA token" (or 403 for
  sessions it already distrusts). The previous version of this script
  posted the form the moment it was submitted, before Shopify's captcha
  had run, so that request always went out without a token and always
  failed: the visitor saw the retry message every time. Meanwhile
  Shopify's own submit handler ran the captcha in parallel. When it passed
  invisibly, Shopify submitted the page natively and it reloaded with the
  overlay closed, so its confirmation was never seen. When it needed a
  visible challenge, the challenge rendered behind this full-screen modal
  dialog, where it could not be reached.

  What this script now does, in order:
    1. Takes the submit first (capture phase + stopImmediatePropagation),
       so exactly one submission path runs.
    2. Asks Shopify's own widget for a token: hcaptcha.execute() on the
       widget Shopify rendered inside this form. If a visible challenge
       appears, the dialog steps aside (closeDialog) so the challenge is on
       top and usable, and comes back (showDialog) once it is answered or
       cancelled.
    3. Posts the form with that token. After 25 s without an answer the
       visitor gets the retry message and their button back, but the
       request is NOT aborted (aborting only stops the browser listening;
       Shopify may already have accepted it). See DUPLICATE GUARDS.
    4. Reports success ONLY on the server's word: the response must contain
       this section's server-rendered .cc-form-success, or the final URL
       must carry contact_posted=true.
    5. If Shopify rejects the captcha (400/403/429), or the widget never
       appears, it hands over to Shopify's own submission: the overlay
       closes so any challenge is visible, and Shopify's form.submit
       (captcha first, then a native POST) takes over.
    6. Every other outcome (server error, network error, timeout, cancelled
       challenge, an unconfirmed response) restores the button and shows
       the existing retry message. Nothing is left on "Sending...".

  After any full-page POST (Shopify's own path, or no JavaScript at all),
  the page reloads with .cc-form-success or .cc-form-error rendered inside
  the overlay; this script opens the overlay on load so the result is seen,
  using the existing confirmation panel for a success.

  DUPLICATE GUARDS (v2). Shopify's contact endpoint has no idempotency
  key, so the only way to avoid a second email is not to send a second
  POST while the first may still succeed:
    - One request in flight at a time. Pressing Send again while it is
      still pending re-attaches to that request instead of posting anew.
    - A late answer still counts: if the slow request later succeeds, the
      confirmation replaces the retry message.
    - Once the form has been handed to Shopify's own submission, it stays
      there: later presses go back to Shopify's path, never to a parallel
      fetch, and the button is only returned after 15 s with no challenge
      on screen and no navigation.

  RETURN KEY. Return/Go in a single-line field moves to the next field;
  it never sends the form. Only the Send button sends.

  RESET ON REOPEN. When the visitor reopens the dialog, the confirmation
  and error are cleared and the form is shown again, as before.
*/

(function () {
  'use strict';

  var SLOW_NOTICE = 25000;    // ms before the visitor is told and given the button back
  var STALL = 15000;          // ms of nothing after a hand-over to Shopify
  var WIDGET_WAIT = 6000;     // ms, for Shopify to bind its hCaptcha widget
  var SILENT_TIMEOUT = 45000; // ms, an execute() that never shows a challenge

  function init() {
    var overlay = document.getElementById('gi-cc-overlay');
    if (!overlay) return;

    var dialog = overlay.querySelector('dialog');
    var form = overlay.querySelector('.cc-form-el');
    var formRoot = overlay.querySelector('.cc-form');
    var confirmEl = overlay.querySelector('.cc-overlay-confirm');
    var errorEl = overlay.querySelector('.cc-overlay-error');
    var canToggle = typeof overlay.showDialog === 'function' && typeof overlay.closeDialog === 'function';

    var programmatic = false; // set while this script reopens the dialog

    overlay.addEventListener('dialog:open', function () {
      if (programmatic) return;
      reset();
    });

    function reset() {
      if (formRoot) formRoot.hidden = false;
      if (confirmEl) confirmEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
    }

    function openProgrammatically() {
      if (!canToggle || dialog.open) return;
      programmatic = true;
      overlay.showDialog();
      setTimeout(function () { programmatic = false; }, 600);
    }

    function showConfirmation() {
      if (formRoot) formRoot.hidden = true;
      if (confirmEl) {
        confirmEl.hidden = false;
        confirmEl.setAttribute('tabindex', '-1');
        confirmEl.focus();
      }
    }

    // A full-page POST came back with a server-rendered result: show it.
    if (overlay.querySelector('.cc-form-success')) {
      openProgrammatically();
      showConfirmation();
    } else if (overlay.querySelector('.cc-form-error')) {
      openProgrammatically();
    }

    if (!form) return;

    var submitBtn = form.querySelector('button[type="submit"]');
    var sendingLabel = submitBtn ? submitBtn.getAttribute('data-sending-label') : null;
    var originalLabel = submitBtn ? submitBtn.textContent : null;
    var busy = false;
    var inFlight = null;     // the pending POST, if any (see DUPLICATE GUARDS)
    var handedOver = false;  // Shopify's own submission has taken over

    function setSending(on) {
      busy = on;
      if (!submitBtn) return;
      submitBtn.disabled = on;
      submitBtn.textContent = on && sendingLabel ? sendingLabel : originalLabel;
    }

    function showError() {
      openProgrammatically();
      setSending(false);
      if (errorEl) errorEl.hidden = false;
    }

    // Shopify's own path: its form.submit runs the captcha, then a native
    // POST. The overlay closes first so a visible challenge is reachable.
    // If Shopify's script is not on the page, this is a plain native POST,
    // exactly what a visitor without JavaScript gets.
    function handOverToShopify() {
      handedOver = true;
      var shopifySubmit = form.submit !== HTMLFormElement.prototype.submit && typeof form.submit === 'function';
      var go = function () {
        if (shopifySubmit) form.submit();
        else HTMLFormElement.prototype.submit.call(form);
      };
      if (canToggle && dialog.open) {
        Promise.resolve(overlay.closeDialog()).then(go, go);
      } else {
        go();
      }
      // If Shopify's challenge is then cancelled, nothing navigates: give
      // the visitor their form back.
      watchForStall();
    }

    function watchForStall() {
      var left = false;
      window.addEventListener('pagehide', function () { left = true; }, { once: true });
      var since = Date.now();
      var timer = setInterval(function () {
        if (left) return clearInterval(timer);
        if (challengeVisible()) { since = Date.now(); return; }
        if (Date.now() - since > STALL) {
          clearInterval(timer);
          showError();
        }
      }, 500);
    }

    /* ── hCaptcha ─────────────────────────────────────────────────────── */

    function widgetId() {
      var frame = form.querySelector('.h-captcha iframe[data-hcaptcha-widget-id]');
      return frame ? frame.getAttribute('data-hcaptcha-widget-id') : null;
    }

    function waitForWidget() {
      return new Promise(function (resolve) {
        var start = Date.now();
        (function poll() {
          var id = window.hcaptcha && typeof window.hcaptcha.execute === 'function' && widgetId();
          if (id) return resolve(id);
          if (Date.now() - start > WIDGET_WAIT) return resolve(null);
          setTimeout(poll, 150);
        })();
      });
    }

    function challengeVisible() {
      var frames = document.querySelectorAll('iframe[title*="hCaptcha challenge"], iframe[src*="frame=challenge"]');
      for (var i = 0; i < frames.length; i++) {
        var f = frames[i];
        if (dialog.contains(f)) continue;
        var r = f.getBoundingClientRect();
        if (r.width < 40 || r.height < 40) continue;
        var el = f, hidden = false;
        while (el && el !== document.body) {
          var cs = window.getComputedStyle(el);
          if (cs.visibility === 'hidden' || cs.display === 'none' || Number(cs.opacity) === 0) { hidden = true; break; }
          el = el.parentElement;
        }
        if (!hidden) return true;
      }
      return false;
    }

    // Resolves with a token, or with null when no widget is available.
    // Rejects when the visitor cancels the challenge, it errors, or it
    // stalls without ever showing a challenge.
    function getToken() {
      return waitForWidget().then(function (id) {
        if (!id) return null;
        try { window.hcaptcha.reset(id); } catch (e) { /* first use */ }
        return new Promise(function (resolve, reject) {
          var steppedAside = false;
          var settled = false;
          var watch = setInterval(function () {
            if (!steppedAside && canToggle && dialog.open && challengeVisible()) {
              steppedAside = true;
              overlay.closeDialog();
            }
          }, 200);
          var silent = setTimeout(function () {
            if (!steppedAside) finish(reject, 'timeout');
          }, SILENT_TIMEOUT);

          function finish(fn, value) {
            if (settled) return;
            settled = true;
            clearInterval(watch);
            clearTimeout(silent);
            if (steppedAside) openProgrammatically();
            fn(value);
          }

          window.hcaptcha.execute(id, { async: true }).then(
            function (res) { finish(resolve, res && res.response ? res.response : null); },
            function (err) { finish(reject, err || 'challenge-error'); }
          );
        });
      });
    }

    /* ── POST ─────────────────────────────────────────────────────────── */

    // 'ok' only on positive evidence from the server. 'captcha' when
    // Shopify rejected the captcha (nothing was accepted). 'error' for
    // everything else, including outcomes where the server MAY have
    // accepted it (5xx, a dropped connection, an unrecognised 200).
    function post(token) {
      var data = new FormData(form);
      if (token) data.set('h-captcha-response', token);

      return fetch(form.getAttribute('action'), {
        method: 'POST',
        body: data,
        headers: { Accept: 'text/html' },
        credentials: 'same-origin'
      })
        .then(function (response) {
          return response.text().then(function (html) {
            var head = html.slice(0, 4000);
            if (response.status === 400 || response.status === 403 || response.status === 429 ||
                /CAPTCHA|Verifying your connection/i.test(head)) return 'captcha';
            if (!response.ok) return 'error';
            if (/[?&]contact_posted=true/.test(response.url) || html.indexOf('cc-form-success') !== -1) return 'ok';
            return 'error';
          });
        })
        .catch(function () { return 'error'; });
    }

    // Sends once and settles the UI. Pressing Send while this is pending
    // joins it (see the submit listener) instead of sending again.
    function send(token) {
      var slow = false;
      var notice = setTimeout(function () {
        slow = true;
        showError(); // the visitor is told and gets the button back
      }, SLOW_NOTICE);

      inFlight = post(token).then(function (result) {
        clearTimeout(notice);
        inFlight = null;
        if (result === 'ok') {
          if (errorEl) errorEl.hidden = true;
          setSending(false);
          return showConfirmation();
        }
        // A captcha refusal means nothing was accepted. Hand over to
        // Shopify only while the visitor is still waiting on this attempt.
        if (result === 'captcha' && !slow) return handOverToShopify();
        showError();
      });
      return inFlight;
    }

    // RETURN KEY MOVES ON, IT DOES NOT SEND (Oct 2026). In a single-line
    // field the browser treats Return/Go as "submit this form". On a phone
    // or tablet that is the key people press to move on or to hide the
    // keyboard, and on this long, mostly optional application it sent a
    // half-finished form. Here Return moves to the next field instead and
    // the keyboard labels it "next"; only the Send button sends. Textareas
    // keep Return as a new line, and nothing is intercepted while an input
    // method (Korean, for one) is still composing.
    var fields = Array.prototype.slice.call(
      form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea')
    );
    fields.forEach(function (el) {
      if (el.tagName === 'INPUT') el.setAttribute('enterkeyhint', 'next');
    });
    form.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) return;
      var el = event.target;
      if (!el || el.tagName !== 'INPUT' || fields.indexOf(el) === -1) return;
      event.preventDefault();
      var next = fields[fields.indexOf(el) + 1];
      if (next) next.focus();
      else if (submitBtn) submitBtn.focus();
    });

    // Capture phase on the form itself: this runs before Shopify's own
    // submit listener (added on first focus) and is the only one that runs.
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      if (errorEl) errorEl.hidden = true;
      setSending(true);

      // A previous request may still succeed: wait for it, do not resend.
      if (inFlight) return;
      // Shopify's own submission already has this form.
      if (handedOver) return handOverToShopify();

      getToken().then(
        function (token) {
          if (!token) return handOverToShopify();
          return send(token);
        },
        function () { showError(); }
      );
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
